/**
 * Admin Orca Events RPC definitions (INF-753)
 *
 * The operator-facing read surface over the cross-task `orca_task_events` log
 * (INF-752): "what has been failing, and why". Two admin-gated, read-only
 * endpoints — global orca telemetry, so `AdminMiddleware` (operator-only), no
 * actor scoping applies:
 *
 *  - `admin.orcaEvents.summary` — event counts grouped by `event_type` over a
 *    `{ sinceHours }` window, plus the last-advance age (minutes since the
 *    newest `phase_transition`) and the current non-terminal task list, each
 *    paired with its latest event. This is the failure-mode rollup that answers
 *    the 2026-08-28 question ("everything healthy, nothing advancing — why?").
 *    It also carries the optional `fixerBuckets` (OL-149): per fixer cause
 *    bucket, how many fixers the window dispatched, over how many distinct
 *    tasks, and how many of those tasks saw the SAME verdict dispatched twice —
 *    the spurious-fixer rate.
 *  - `admin.orcaEvents.list` — a `{ taskId?, eventType?, limit }` drill-down for
 *    a single task or event class. `limit` is schema-bounded 1-500 so a bad
 *    client can't ask for an unbounded scan.
 *
 * Timestamps are `Schema.DateTimeUtc`; event rows wire as {@link OrcaTaskEvent}
 * `Model.json` (the single source of truth from `@fm/db`).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { OrcaTaskPhase } from "@fm/db/models/OrcaTask";
import { OrcaTaskEvent, OrcaTaskEventType } from "@fm/db/models/OrcaTaskEvent";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";

// ============================================================================
// Request schemas
// ============================================================================

/**
 * `summary` window. `sinceHours` bounds the group-by scan to the last N hours
 * (1h..1y) so the rollup never degenerates into a full-table scan.
 *
 * @since 1.0.0
 */
export const OrcaEventsSummaryInput = Schema.Struct({
  sinceHours: Schema.Int.pipe(Schema.between(1, 8760)),
});
export type OrcaEventsSummaryInput = Schema.Schema.Type<typeof OrcaEventsSummaryInput>;

/**
 * `list` drill-down filters. `taskId` / `eventType` narrow the newest-first
 * scan; `limit` is bounded 1-500 (a value outside the range is rejected by the
 * schema before the handler runs).
 *
 * @since 1.0.0
 */
export const OrcaEventsListInput = Schema.Struct({
  taskId: Schema.optional(Schema.String),
  eventType: Schema.optional(OrcaTaskEventType),
  limit: Schema.Int.pipe(Schema.between(1, 500)),
});
export type OrcaEventsListInput = Schema.Schema.Type<typeof OrcaEventsListInput>;

// ============================================================================
// Response schemas
// ============================================================================

/**
 * One `{ eventType, count }` bucket in the failure-mode rollup.
 *
 * @since 1.0.0
 */
export const OrcaEventTypeCount = Schema.Struct({
  eventType: OrcaTaskEventType,
  count: Schema.Int,
});
export type OrcaEventTypeCount = Schema.Schema.Type<typeof OrcaEventTypeCount>;

/**
 * One fixer cause bucket's rollup over the summary window (OL-149):
 *
 *  - `dispatches` — how many fixers this bucket dispatched,
 *  - `tasks` — over how many DISTINCT tasks,
 *  - `repeatedVerdictTasks` — how many of those tasks had the same verdict key
 *    dispatched twice or more, i.e. a fixer that did not fix what it was sent
 *    for. This is the spurious-fixer numerator Jeff reads every 24 hours.
 *
 * `bucket` is a plain `Schema.String` on the wire: the `FixerBucket` literal
 * union lives in `services/orca/fixer-verdict.ts`, and a renderer should not
 * have to import api internals (nor break decoding when a new bucket ships
 * before the client knows about it).
 *
 * @since 1.0.0
 */
export const FixerBucketCount = Schema.Struct({
  bucket: Schema.String,
  dispatches: Schema.Int,
  tasks: Schema.Int,
  repeatedVerdictTasks: Schema.Int,
});
export type FixerBucketCount = Schema.Schema.Type<typeof FixerBucketCount>;

/**
 * A non-terminal task paired with its latest recorded event (`null` when the
 * task has no event rows yet). `phaseEnteredAt` shows how long it has sat in its
 * current phase.
 *
 * @since 1.0.0
 */
export const NonTerminalTaskEvent = Schema.Struct({
  taskId: Schema.String,
  linearIssueIdentifier: Schema.String,
  phase: OrcaTaskPhase,
  phaseEnteredAt: Schema.DateTimeUtc,
  latestEvent: Schema.NullOr(OrcaTaskEvent.json),
});
export type NonTerminalTaskEvent = Schema.Schema.Type<typeof NonTerminalTaskEvent>;

/**
 * One `{ name, count }` bucket of the recovery-playbook rollup (OL-168): how
 * many times each named recovery was applied over the window. `name` is the
 * `recovery_applied` event's `reason`; rows recorded without one group under
 * the literal `"unknown"` rather than being dropped.
 *
 * @since 1.0.0
 */
export const OrcaRecoveryNameCount = Schema.Struct({
  name: Schema.String,
  count: Schema.Int,
});
export type OrcaRecoveryNameCount = Schema.Schema.Type<typeof OrcaRecoveryNameCount>;

/**
 * FAILED → PASSED dwell over the window (OL-168) — the recovery playbook's
 * headline metric. `count` is how many recoveries were measured; both
 * percentiles are whole minutes, and `null` when `count` is 0.
 *
 * @since 1.0.0
 */
export const OrcaFailedToPassedDwell = Schema.Struct({
  count: Schema.Int,
  p50Minutes: Schema.NullOr(Schema.Int),
  meanMinutes: Schema.NullOr(Schema.Int),
});
export type OrcaFailedToPassedDwell = Schema.Schema.Type<typeof OrcaFailedToPassedDwell>;

/**
 * The `summary` payload: the grouped counts, the last-advance age, and the
 * current non-terminal task list with each task's latest event.
 *
 * `lastAdvanceAt` / `lastAdvanceAgeMinutes` are `null` when no `phase_transition`
 * event exists yet (a fresh pipeline with no advance baseline).
 *
 * `stallThresholdMinutes` is the server-owned `ORCA_STALL_ALERT_MINUTES` (default
 * 30) the background {@link OrcaPipelineStallDetector} alerts on, so the "Last
 * advance" tile and the stalled banner render from server truth rather than a
 * client-side constant. `stalled` is `true` iff `nonTerminalTasks` is non-empty
 * (there is live work that could advance) AND `lastAdvanceAgeMinutes` is
 * non-null and `>= stallThresholdMinutes` — an idle pipeline is never stalled,
 * matching the detector's own alert rule.
 *
 * `recoveries` and `failedToPassedDwell` (OL-168) make the recovery playbook's
 * effect measurable: which named recoveries fired over the window, and how long
 * tasks sat in FAILED before reaching PASSED. Both are `Schema.optional` so a
 * client pinned to the pre-OL-168 payload still decodes, and so the page can
 * render `—` rather than crash when a server omits them.
 * `fixerBuckets` is the per-cause fixer-dispatch rollup over the same window,
 * one entry per {@link FixerBucket} in `FIXER_BUCKETS` order (zeros included, so
 * a tile never disappears between windows). It is OPTIONAL: the web package
 * decodes `OrcaEventsSummary` and ships its own typed fixtures, which keep
 * compiling and decoding until OL-150 renders the field.
 *
 * @since 1.0.0
 */
export const OrcaEventsSummary = Schema.Struct({
  sinceHours: Schema.Int,
  countsByType: Schema.Array(OrcaEventTypeCount),
  lastAdvanceAt: Schema.NullOr(Schema.DateTimeUtc),
  lastAdvanceAgeMinutes: Schema.NullOr(Schema.Int),
  stallThresholdMinutes: Schema.Int,
  stalled: Schema.Boolean,
  nonTerminalTasks: Schema.Array(NonTerminalTaskEvent),
  recoveries: Schema.optional(Schema.Array(OrcaRecoveryNameCount)),
  failedToPassedDwell: Schema.optional(OrcaFailedToPassedDwell),
  fixerBuckets: Schema.optional(Schema.Array(FixerBucketCount)),
});
export type OrcaEventsSummary = Schema.Schema.Type<typeof OrcaEventsSummary>;

/**
 * The `list` payload: newest-first event rows.
 *
 * @since 1.0.0
 */
export const OrcaEventsList = Schema.Array(OrcaTaskEvent.json);
export type OrcaEventsList = Schema.Schema.Type<typeof OrcaEventsList>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca Events RPC group. Both endpoints require admin role via
 * `AdminMiddleware`; tags are the full dot-namespaced names (mirroring
 * {@link AdminOrcaTaskRpcs}).
 *
 * @since 1.0.0
 */
export class AdminOrcaEventsRpcs extends RpcGroup.make(
  Rpc.make("admin.orcaEvents.summary", {
    payload: OrcaEventsSummaryInput,
    success: OrcaEventsSummary,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaEvents.list", {
    payload: OrcaEventsListInput,
    success: OrcaEventsList,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
